import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import FindTalentsContainer from '../../components/FindTalentsContainer';
import { MetaTags } from '../../components/Metatags/Metatags';
import { CustomPagination } from '../../components/Tables/Pagination';
import { usePagination } from '../../hook/usePagination';
import { useDispatchPromise } from '../../utils/AppUtils';
import { findTalent } from '../../redux/talent/actions';
import { ITalentDiscoverResponse, ITalentFilters } from '../../redux/talent/types';
import { useSelector } from 'react-redux';
import { loadingSelector, talentsSelector } from './selector';
import { Container } from '@mui/material';

const FindTalents = () => {
    const { pagination, setPagination, handleRowsChange, handlePageChange } = usePagination(1, 12);
    const talents = useSelector(talentsSelector);
    const loading = useSelector(loadingSelector);
    const dispatch = useDispatchPromise();
    const [searchParams, setSearchParams] = useSearchParams();
    const [localFilters, setLocalFilters] = useState<ITalentFilters>({});

    // Load filters from URL on mount
    useEffect(() => {
        const params = Object.fromEntries([...searchParams]);
        const urlFilters: ITalentFilters = {};

        // Convert URL params to filters
        Object.entries(params).forEach(([key, value]) => {
            if (key === 'ageFrom' || key === 'ageTo' || key === 'rating' || key === 'page' || key === 'limit') {
                urlFilters[key] = Number(value);
            } else if (key === 'languages' || key === 'skills' || key === 'ethnicity' || key === 'language_spoken') {
                urlFilters[key] = value.split(',');
            } else if (key === 'lat' || key === 'lng') {
                urlFilters[key] = Number(value);
            } else {
                urlFilters[key as keyof ITalentFilters] = value as any;
            }
        });

        setLocalFilters(urlFilters);
    }, [searchParams]);

    const getTalents = useCallback(
        async (limit?: number, page?: number, filters?: ITalentFilters) => {
            const currentFilters = filters || localFilters;
            if (!loading) {
                const response: ITalentDiscoverResponse = (await dispatch(
                    findTalent({
                        ...currentFilters,
                        limit: limit ?? pagination.rows,
                        page: page ?? pagination.page,
                    }),
                )) as ITalentDiscoverResponse;
                const { currentPage, pageSize, totalCount } = response;
                if (currentPage) {
                    setPagination({
                        page: currentPage,
                        rows: pageSize,
                        totalCount: totalCount,
                    });
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [localFilters, pagination.rows, pagination.page, setPagination],
    );

    // Initial load
    useEffect(() => {
        getTalents();
    }, [getTalents]);

    const handleSearch = useCallback(
        (searchValue: string) => {
            const newFilters = { ...localFilters, searchTerm: searchValue };
            setLocalFilters(newFilters);

            // Always build fresh URLSearchParams from scratch
            const params = new URLSearchParams();
            Object.entries(newFilters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    if (Array.isArray(value)) {
                        params.set(key, value.join(','));
                    } else {
                        params.set(key, String(value));
                    }
                }
            });

            setSearchParams(params);
            getTalents(undefined, undefined, newFilters);
        },
        [localFilters, setSearchParams, getTalents],
    );

    const handleFilterChange = useCallback(
        (filters: ITalentFilters) => {
            setLocalFilters(filters);

            const params = new URLSearchParams();
            Object.entries(filters).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== '') {
                    if (Array.isArray(value)) {
                        params.set(key, value.join(','));
                    } else {
                        params.set(key, String(value));
                    }
                }
            });

            setSearchParams(params);
            getTalents(undefined, undefined, filters);
        },
        [setSearchParams, getTalents],
    );

    const handleClearFilters = () => {
        const preservedFilters: ITalentFilters = {};

        // Preserve 'profile_type' if it exists
        if (localFilters.profile_type) {
            preservedFilters.profile_type = localFilters.profile_type;
        }

        setLocalFilters(preservedFilters);

        // Create new search params with only 'profile_type'
        const params = new URLSearchParams();
        if (preservedFilters.profile_type) {
            params.set('profile_type', String(preservedFilters.profile_type));
        }

        setSearchParams(params);
        getTalents(undefined, undefined, preservedFilters);
    };

    return (
        <MetaTags title={`Discover Talents`} description={'Discover Talents'}>
            <Container maxWidth={false}>
                <FindTalentsContainer
                    talents={talents}
                    filters={localFilters}
                    totalCount={pagination.totalCount}
                    locationLabel={localFilters?.location}
                    loading={loading}
                    onSearch={handleSearch}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                    customPagination={
                        <CustomPagination
                            pagination={pagination}
                            onRowsChange={handleRowsChange}
                            onPageChange={handlePageChange}
                            rowsPerPageOptions={[12, 48, 96, 120]}
                        />
                    }
                />
            </Container>
        </MetaTags>
    );
};

export { FindTalents };
